import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { SEARCH_TYPES, searchEverything } from '@/api/search';
import { useAuth } from '@/auth/useAuth';
import { useModules } from '@/modules/ModuleContext';
import { SearchCombobox, type SearchComboboxOption } from '@/ui/SearchCombobox';
import { readRecentRecords, rememberRecentRecord } from './recentRecords';
import {
  nothingFound,
  pageGroups,
  recentGroups,
  recordGroups,
  searchableTypes,
  type SearchChoice,
} from './searchModel';

/** How many hits of each kind the list shows before "See all". */
const HITS_PER_TYPE = 4;

/** The longest search the server accepts. */
const MAX_TERM_LENGTH = 100;

/** The working surface, where focus goes once something has been opened. */
const MAIN_ID = 'main';

function shortcutHint(): string {
  const onApple =
    typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/i.test(navigator.userAgent);
  return onApple ? '⌘K' : 'Ctrl K';
}

/**
 * The search box in the command strip: pages by name at once, and records from the server.
 *
 * One box finds a customer, a job, a deal, a lead, a proposal, an invoice, a material, a service or
 * a person, and lists what it found under the name of each kind. Enter opens the top hit, the
 * arrow keys move through the list, and Escape clears it. Before anything is typed it lists the
 * records opened lately, which this browser remembers for the person who is signed in.
 *
 * It also offers the next step where there is one: booking a job for the customer found, and
 * creating a customer when nobody matches.
 *
 * Ctrl K, or ⌘K, focuses it from anywhere.
 *
 * @returns the search landmark
 */
export function GlobalSearch(): React.JSX.Element {
  const { can, identity, user } = useAuth();
  const enabled = useModules();
  const navigate = useNavigate();
  const input = useRef<HTMLInputElement>(null);
  const [recents, setRecents] = useState(() => readRecentRecords(identity));
  const access = useMemo(() => ({ can, enabled }), [can, enabled]);
  const zone = user?.workspaceTimeZone ?? '';
  const [shortcut] = useState(shortcutHint);

  useEffect(() => {
    const focusSearch = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        input.current?.focus();
      }
    };
    window.addEventListener('keydown', focusSearch);
    return () => {
      window.removeEventListener('keydown', focusSearch);
    };
  }, []);

  const search = async (term: string, signal: AbortSignal) => {
    const types = searchableTypes(enabled);
    const results = await searchEverything(term, {
      ...(types.length === SEARCH_TYPES.length ? {} : { types }),
      limit: HITS_PER_TYPE,
      signal,
    });
    return recordGroups(results, { term, access, zone });
  };

  const choose = ({ data }: SearchComboboxOption<SearchChoice>): void => {
    if (data.remember !== undefined) {
      rememberRecentRecord(identity, data.remember);
      setRecents(readRecentRecords(identity));
    }
    void navigate(data.to);
    document.getElementById(MAIN_ID)?.focus();
  };

  return (
    <div className="fo-strip__search" role="search">
      <SearchCombobox<SearchChoice>
        aria-label="Search"
        placeholder="Search"
        icon="search"
        trailing={<kbd>{shortcut}</kbd>}
        inputRef={input}
        maxLength={MAX_TERM_LENGTH}
        searchScope={identity ?? ''}
        search={search}
        instant={(term) => pageGroups(term, access)}
        idle={recentGroups(recents, enabled)}
        empty={(term) => nothingFound(term, access)}
        onSelect={choose}
      />
    </div>
  );
}
